// Minimized games — a browser-local "I've ruled this one out" list for the
// Value Dashboard's College Football lens (#654).
//
// Where a saved game is PROMOTED (#120 pins it to a shortlist above the board),
// a minimized game is DEMOTED: dropped from the board so the remaining slate is
// what's still worth evaluating. CFB is what forced it — a full prod-hydra pull
// on 2026-08-20 returned 184 games in one day.
//
// Presentation preference ONLY: nothing here reaches the relay, the org, or
// another device, and no price is ever persisted.
//
// ── Identity ────────────────────────────────────────────────────────────
// Exactly the saved list's rules, from the shared `gameMarkIdentity` module: a
// doubleheader's two games are two marks, one game on two venues is one mark,
// and a row that cannot express a game number falls back to the collapsed key.
// A trader should not have to hold two mental models for two lists that both
// answer "which game is this?".
//
// ── Why only ids are stored ─────────────────────────────────────────────
// The saved list persists display metadata (sport, teams, date) because it
// renders saved games that have fallen off the board — SavedUnlistedCard. A
// minimized game that falls off the board is already invisible and needs no
// row, so there is nothing to render from and nothing to store beyond the id.
//
// ── Why the list prunes itself ──────────────────────────────────────────
// A minimized game is never revisited, so without a bound this list would grow
// for the lifetime of the browser profile. Entries age out by GAME date (read
// back out of the id) rather than by when they were marked: a game from six
// weeks ago is over, whenever the user dismissed it.
//
// Writes are best-effort: a full or unavailable localStorage (private mode)
// degrades to in-memory only rather than throwing, matching savedGames.

import { create } from 'zustand';
import { storageKey } from '@/lib/infra/storage';
import {
  collapsedGameKey,
  gameDateFromMarkId,
  gameMarkId,
  isSportBearing,
  ownedBy,
  type MarkableGame,
} from './gameMarkIdentity';

/** How long a minimized mark outlives its game's date. Long enough that a
 *  postponed or rescheduled game keeps the trader's decision, short enough that
 *  the list can't grow without bound. */
const PRUNE_AFTER_DAYS = 30;

const EMPTY_IDS: ReadonlySet<string> = new Set<string>();

function storageKeyFor(userId: string): string {
  return storageKey(`galactus:minimizedGames:${userId}`);
}

/** The oldest game date a mark may refer to and still be kept, as YYYY-MM-DD.
 *  Local-date arithmetic, matching how the dashboard's own date filters treat
 *  a game's calendar day. */
function pruneCutoffIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - PRUNE_AFTER_DAYS);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Drop marks whose game is long past. An id whose date can't be parsed is
 *  KEPT: it is un-prunable rather than expired, and silently discarding marks
 *  we merely failed to read would hide games the user chose to dismiss. */
function prune(ids: string[]): string[] {
  const cutoff = pruneCutoffIso();
  return ids.filter((id) => {
    const date = gameDateFromMarkId(id);
    return date == null || date >= cutoff;
  });
}

function load(userId: string): string[] {
  try {
    const raw = localStorage.getItem(storageKeyFor(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Drop anything malformed rather than letting one bad entry poison the
    // whole list — a minimized game the store can't read is also one the user
    // can never restore.
    return prune(parsed.filter((x): x is string => typeof x === 'string' && x !== ''));
  } catch {
    // Unavailable / malformed localStorage — start empty.
    return [];
  }
}

function persist(userId: string, ids: string[]): void {
  try {
    localStorage.setItem(storageKeyFor(userId), JSON.stringify(ids));
  } catch {
    // localStorage full or unavailable — in-memory state still works for this
    // session. Silent, same as savedGames.
  }
}

/** Both membership indexes for a list. Rebuilt on every mutation so zustand
 *  subscribers re-render. */
function indexOf(ids: string[]): {
  ids: ReadonlySet<string>;
  collapsedIds: ReadonlySet<string>;
} {
  const exact = new Set<string>();
  const collapsed = new Set<string>();
  for (const id of ids) {
    exact.add(id);
    const c = collapsedGameKey(id);
    if (c) collapsed.add(c);
  }
  return { ids: exact, collapsedIds: collapsed };
}

/**
 * True when `rowKey` refers to a game the user has minimized.
 *
 * Same two matching rules as isRowSaved, for the same reason: a live row
 * matches on the exact doubleheader-safe id, while a sport-less row projected
 * from the analytics history can only match on the collapsed key. Ownership is
 * checked first, so nothing about another account's list reaches the screen.
 */
export function isRowMinimized(
  rowKey: string | null | undefined,
  state: {
    userId: string | null;
    ids: ReadonlySet<string>;
    collapsedIds: ReadonlySet<string>;
  },
  viewerId: string | null,
): boolean {
  if (!ownedBy(state, viewerId)) return false;
  if (isSportBearing(rowKey)) {
    const id = gameMarkId(rowKey);
    return id != null && state.ids.has(id);
  }
  const collapsed = collapsedGameKey(rowKey);
  return collapsed != null && state.collapsedIds.has(collapsed);
}

/**
 * Clear the in-memory minimized list. Persisted lists are deliberately left
 * intact, so signing back in restores your own decisions.
 *
 * Called from authStore.purgeUserCaches alongside resetSavedGames, which runs
 * on EVERY path to unauthenticated — including the cross-tab storage handler.
 */
export function resetMinimizedGames(): void {
  useMinimizedGames.getState().hydrateFor(null);
}

interface MinimizedGamesState {
  /** Whose list is currently loaded. Null when signed out. */
  userId: string | null;
  /** Insertion-ordered marks, as persisted. */
  order: string[];
  /** Exact-id membership — the doubleheader-safe lookup. */
  ids: ReadonlySet<string>;
  /** Collapsed-key membership, for rows that can't express a game number. */
  collapsedIds: ReadonlySet<string>;
  /** Point the store at a user's list, pruning marks whose games are long
   *  past. Call whenever the signed-in user changes; passing null clears the
   *  in-memory list (localStorage is left intact so signing back in restores
   *  it). */
  hydrateFor: (userId: string | null) => void;
  /** Minimize the game if it isn't minimized, restore it if it is. `viewerId`
   *  is the signed-in user the action is on behalf of; a mismatch against the
   *  loaded list's owner is a no-op, so one account's click can never land in
   *  another's list. No-op too when the row has no canonical key. */
  toggle: (game: MarkableGame, viewerId: string | null) => void;
  /** Restore every minimized game at once — the board footer's "Show all".
   *  This is the recovery affordance the lens contract requires, so it must
   *  stay reachable from the surface that does the hiding. */
  restoreAll: (viewerId: string | null) => void;
}

export const useMinimizedGames = create<MinimizedGamesState>((set, get) => ({
  userId: null,
  order: [],
  ids: EMPTY_IDS,
  collapsedIds: EMPTY_IDS,

  hydrateFor: (userId) => {
    if (get().userId === userId) return;
    if (!userId) {
      set({ userId: null, order: [], ids: EMPTY_IDS, collapsedIds: EMPTY_IDS });
      return;
    }
    const order = load(userId);
    set({ userId, order, ...indexOf(order) });
  },

  toggle: (game, viewerId) => {
    const state = get();
    const { userId, order } = state;
    if (!userId || !ownedBy(state, viewerId)) return;

    let next: string[];

    if (isSportBearing(game.key)) {
      const id = gameMarkId(game.key);
      if (!id) return;
      next = order.includes(id) ? order.filter((x) => x !== id) : [...order, id];
    } else {
      // Sport-less row (analytics archived). It can't name a specific game of a
      // doubleheader, so it restores every mark it could be referring to — that
      // one row IS both of them on screen.
      const collapsed = collapsedGameKey(game.key);
      if (!collapsed) return;
      const already = order.some((x) => collapsedGameKey(x) === collapsed);
      next = already
        ? order.filter((x) => collapsedGameKey(x) !== collapsed)
        : [...order, collapsed];
    }

    commit(set, userId, next);
  },

  restoreAll: (viewerId) => {
    const state = get();
    const { userId, order } = state;
    if (!userId || !ownedBy(state, viewerId)) return;
    if (order.length === 0) return;
    commit(set, userId, []);
  },
}));

function commit(
  set: (partial: Partial<MinimizedGamesState>) => void,
  userId: string,
  next: string[],
): void {
  set({ order: next, ...indexOf(next) });
  persist(userId, next);
}
